// @ts-nocheck
import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import config from 'config';
import { isEmpty } from 'lodash';

import applicationActions from 'common/support/applicationActions';

import { pageSelected } from 'actions/pageView';

import { useFocusManager } from 'hooks/focus';
import { useRestoration } from 'hooks/restoration';
import { useImperativeTimer } from 'hooks/timeout';
import useTracking from 'hooks/analytics';

import { fetchSport, clearSport } from 'actions/sports';
import { fetchLeague, clearLeague } from 'actions/leagues';

import { fetchShow, clearShow } from 'actions/shows';
import { fetchMarket, clearMarket } from 'actions/localMarkets';

import textToSpeechManager from 'support/tts/TextToSpeechManager';
import {
  getPageValues,
  createBreadCrumb,
} from 'support/analytics/analyticsHelper';

import InteriorRows from 'components/pages/home/InteriorRows';
import PageHeader from 'components/pages/home/PageHeader';
import { FocusSettingsProvider } from 'components/focus/FocusSettingsContext';

import {
  selectSportByTag,
  selectLeagueByTag,
  selectShowByTag,
  selectLocalMarketByTag,
  selectTitle,
  selectHeaderImage,
  selectDescription,
  selectRowData,
  selectRelatedLeagues,
  selectRelatedShows,
  selectRelatedMarkets,
  selectLogoImage,
} from 'selectors/dataByTag';

import { FocusGroup } from 'components/focus';
import WithLoading from 'components/common/WithLoading';

import styles from './InteriorPage.module.scss';

const messages = {
  noData: {
    id: 'page.noData.message',
    description: 'message displayed when there is no data',
  },
  sportsTts: {
    id: 'page.tts.surfaceSports',
    description: 'Tts for when the sports page is mounted',
  },
  leaguesRowTitleTts: {
    id: 'tile.leagues.tts.rowTitle',
    description: 'Leagues tts for row title',
  },
};

// pageName constants
const SPORTS = 'sports';
const LEAGUES = 'leagues';
const MARKETS = 'markets';
const SHOWS = 'shows';

const RestorationStatus = {
  RESTORED: 'restored',
  STARTED: 'started',
  UNRESTORED: 'unrestored',
};

const trackInteriorPage = (tracker, { content, pageType, historyStack }) => {
  const pageData = {
    pageType,
    ...getPageValues(content),
    breadCrumb: createBreadCrumb(historyStack),
  };
  tracker(pageData);
};

const useInteriorPageTTS = (title, description, rows, footerRef, pageName) => {
  const focusManager = useFocusManager();
  const intl = useIntl();

  const firstTtsCall = useRef(true);

  useEffect(() => {
    const onFocusChangedListener = (newId, prevId) => {
      const isLeagueRow = newId.includes(LEAGUES);
      const focusedTTS = textToSpeechManager.getTTSText(newId);
      if (
        !focusedTTS ||
        (!focusManager.isFocusAncestor(`${pageName}-focusable-content`) &&
          !isLeagueRow)
      )
        return;

      const getRowId = id => {
        // Check for null id. In an edge case, 'prevId' could be null if user click
        // a media tile before initial focus is set
        if (!id) return null;
        const regex = /\brow-([^-]+)/;
        const match = id.match(regex);
        return match?.[1];
      };

      if (firstTtsCall.current) {
        textToSpeechManager.add(
          intl.formatMessage(messages.sportsTts, { pagename: title }),
        );
        textToSpeechManager.add(description);
        firstTtsCall.current = false;
      }

      const newRowId = getRowId(newId);
      const prevRowId = getRowId(prevId);

      if (newRowId !== prevRowId) {
        if (isLeagueRow) {
          textToSpeechManager.add(
            intl.formatMessage(messages.leaguesRowTitleTts),
          );
        } else {
          const newRow = rows?.find(row => row.id === newRowId);

          if (newRow?.title) {
            textToSpeechManager.add(newRow.title);
          }
        }
      }
      textToSpeechManager.add(focusedTTS);

      if (
        pageName === SPORTS &&
        newId.includes('lastrow') &&
        !prevId.includes('lastrow')
      ) {
        textToSpeechManager.add(footerRef.current?.getTtsText());
      }

      // leagues only
      if (
        pageName === LEAGUES &&
        newId.includes(LEAGUES) &&
        !prevId.includes(LEAGUES)
      ) {
        textToSpeechManager.add(footerRef.current?.getTtsText());
      }

      textToSpeechManager.speak();
    };

    focusManager.addFocusChangedListener(onFocusChangedListener);

    return () => {
      focusManager.removeFocusChangedListener(onFocusChangedListener);
    };
  }, [focusManager, intl, title, description, rows, footerRef, pageName]);

  return { enableTTSOnFocus: false };
};

const useInteriorPageRestoration = (isReady, title, pageName) => {
  const { restorationState, setRestorationState } = useRestoration(title);
  const focusManager = useFocusManager();

  const restoredFocusId = restorationState?.focusId;

  const [restorationStatus, setRestorationStatus] = useState(
    RestorationStatus.STARTED,
  );

  useEffect(() => {
    if (isReady && restorationStatus === RestorationStatus.STARTED) {
      if (
        restoredFocusId &&
        focusManager.getFocusableElement(restoredFocusId)
      ) {
        focusManager.setCurrentFocus(restoredFocusId);
        setRestorationStatus(RestorationStatus.RESTORED);
      } else {
        setRestorationStatus(RestorationStatus.UNRESTORED);
      }
    }
  }, [focusManager, isReady, restoredFocusId, restorationStatus]);

  // update here
  useEffect(() => {
    const onFocusChangedListener = () => {
      if (focusManager.isFocusAncestor(`${pageName}-focusable-content`)) {
        setRestorationState({
          ...restorationState,
          focusId: focusManager.currentFocusId,
        });
      }
    };
    focusManager.addFocusChangedListener(onFocusChangedListener);
    return () => {
      focusManager.removeFocusChangedListener(onFocusChangedListener);
    };
  }, [focusManager, restorationState, setRestorationState, pageName]);

  return [restorationStatus];
};

const InteriorPage = ({ pageName, pageType }) => {
  const { contentId } = useParams();
  const dispatch = useDispatch();
  const intl = useIntl();
  const focusManager = useFocusManager();
  const { trackPage: tracker } = useTracking();
  const history = useHistory();
  const auth = useSelector(state => state.auth);

  useEffect(() => {
    if (contentId) {
      if (pageName === SPORTS) {
        dispatch(fetchSport(contentId));
      } else if (pageName === LEAGUES) {
        dispatch(fetchLeague(contentId));
      } else if (pageName === SHOWS) {
        dispatch(fetchShow(contentId));
      } else if (pageName === MARKETS) {
        dispatch(fetchMarket(contentId));
      }
    } else if (pageType === 'tagrow' && pageName === 'all-leagues') {
      // all leagues page
    } else if (pageType === 'tagrow' && pageName === 'all-shows') {
      // all shows page
    }
  }, [dispatch, contentId, pageName, pageType]);

  const dataSports = useSelector(state => selectSportByTag(state, contentId));
  const dataLeagues = useSelector(state => selectLeagueByTag(state, contentId));
  const dataShows = useSelector(state => selectShowByTag(state, contentId));
  const dataLocalMarkets = useSelector(state =>
    selectLocalMarketByTag(state, contentId),
  );
  const loadingSports = useSelector(state => state.sports?.loading);
  const loadingLeagues = useSelector(state => state.leagues?.loading);
  const loadingShows = useSelector(state => state.shows?.loading);
  const loadingLocalMarkets = useSelector(state => state.localMarkets?.loading);

  const data = (() => {
    let dataValue;
    if (pageName === SPORTS) {
      dataValue = dataSports;
    } else if (pageName === LEAGUES) {
      dataValue = dataLeagues;
    } else if (pageName === SHOWS) {
      dataValue = dataShows;
    } else if (pageName === MARKETS) {
      dataValue = dataLocalMarkets;
    }
    return dataValue;
  })();

  const loading = (() => {
    let loadingValue;
    if (pageName === SPORTS) {
      loadingValue = loadingSports;
    } else if (pageName === LEAGUES) {
      loadingValue = loadingLeagues;
    } else if (pageName === SHOWS) {
      loadingValue = loadingShows;
    } else if (pageName === MARKETS) {
      loadingValue = loadingLocalMarkets;
    }
    return loadingValue;
  })();

  const title = selectTitle(data);
  const description = selectDescription(data);
  const [headerImageSrc, isHeaderAnimated] = selectHeaderImage(data);
  const rowData = selectRowData(data);
  const relatedLeagues = selectRelatedLeagues(data);
  const relatedShows = selectRelatedShows(data);
  const relatedMarkets = selectRelatedMarkets(data);
  // for leagues only
  const logoSrc = selectLogoImage(data);

  const footerRef = useRef();
  // useInteriorPageTTS must be called before all other focus impacting hooks
  const { enableTTSOnFocus } = useInteriorPageTTS(
    title,
    description,
    rowData,
    footerRef,
    pageName,
  );

  const [rowIndex, setRowIndex] = useState(0);

  // Handles the initial focus setting logic
  const hasFocusBeenInitialized = useRef(false);
  const [restoredFocus] = useInteriorPageRestoration(!loading, title, pageName);

  useEffect(() => {
    dispatch(pageSelected(contentId, pageType));
  }, [dispatch, contentId, pageType]);

  const { push } = useHistory();
  const navigateToPlayer = useCallback(
    (rowId, videoId, rowSlug, video) => {
      const params = new URLSearchParams();
      if (videoId) params.set('video', videoId);
      if (rowId) params.set('row', rowId);
      if (rowSlug) params.set('rowSlug', rowSlug);

      const search = params.toString();

      if (
        auth.paramountPlus?.user?.data?.userPackage ===
          'CBS_ALL_ACCESS_LOW_COST_PACKAGE' &&
        (auth.adobe?.authN == null || !video?.authTypes.includes('adobe')) &&
        !video?.authenticationDetails?.paramountplus?.essentials &&
        video?.authenticationDetails?.paramountplus?.premium
      ) {
        // user auth is p+ essentials
        // user is either not logged in with mvpd or (if they are logged in with mvpd) the stream is not mvpd authorized
        // content is p+ premium only
        // show upgrade to watch screen
        push({
          pathname: '/upgradeToWatch',
          ...(search ? { search } : {}),
        });
      } else {
        push({
          pathname: '/playback',
          ...(search ? { search } : {}),
        });
      }
    },
    [push, auth],
  );

  const idleInterval = useSelector(
    state =>
      state.kitchenSink?.data?.config?.modules?.timeouts?.params
        ?.browseModeTimeout,
  );
  const idleTime = idleInterval * 1000;

  const timerRef = useRef();

  timerRef.current = useImperativeTimer(() => {
    if (data) {
      navigateToPlayer();
    }
  }, idleTime || config.playback.idleToFullScreenMs);

  useEffect(() => {
    timerRef.current.restart();
  }, []);

  useEffect(() => {
    const onFocusChangedListener = () => {
      timerRef.current.restart();
    };
    focusManager.addFocusChangedListener(onFocusChangedListener);
    return () => {
      focusManager.removeFocusChangedListener(onFocusChangedListener);
    };
  }, [focusManager]);

  useEffect(() => {
    if (restoredFocus === RestorationStatus.UNRESTORED) {
      if (!hasFocusBeenInitialized.current) {
        hasFocusBeenInitialized.current = true;
      }
    }
  }, [focusManager, restoredFocus]);

  useEffect(() => {
    if (restoredFocus === RestorationStatus.RESTORED) {
      hasFocusBeenInitialized.current = true;
    }
  }, [restoredFocus]);

  useEffect(() => {
    if (!loading && hasFocusBeenInitialized.current) {
      if (rowData?.length > 0) {
        focusManager.setCurrentFocus('interior-rows');
      } else {
        focusManager.setCurrentFocus(`${pageName}-focusable-content`);
      }
    }
  }, [focusManager, rowData, loading, pageName]);

  useEffect(() => {
    trackInteriorPage(tracker, {
      content: contentId,
      pageType,
      historyStack: history.entries,
    });
  }, [tracker, contentId, pageType, history]);

  const onTileClick = useCallback(
    tileProps => {
      const { rowId, video, rowSlug } = tileProps;
      navigateToPlayer(rowId, video.id, rowSlug, video);
    },
    [navigateToPlayer],
  );

  const onInteriorRowsFocusIndexChanged = useCallback(index => {
    setRowIndex(index);
  }, []);

  // only for leagues page
  const cameFromSportsPage = useSelector(state => state.sports?.data);
  const goBack = action => {
    if (action === applicationActions.back) {
      dispatch(clearLeague());
      if (!isEmpty(cameFromSportsPage)) {
        // TODO: Will need to revisit this if we are allowing deeplinking to Sports pages
        // possibly check history.entries to see whether the first entry is home
        const sportsPage = 2 - history.length;
        history.go(sportsPage);
        return true;
      }
      const home = -1 * history.length;
      history.go(home);
      return true;
    }

    return false;
  };
  // end of section

  return (
    <WithLoading loading={loading}>
      <FocusSettingsProvider enableTTSOnFocus={enableTTSOnFocus}>
        {!loading && (
          <FocusGroup
            focusId={`${pageName}-focusable-content`}
            className="w-100 h-100 position-relative"
            // need to define pageName
            onActionOverride={action => {
              if (action === applicationActions.back) {
                if (pageName === SPORTS) {
                  dispatch(clearSport());
                } else if (pageName === SHOWS) {
                  dispatch(clearShow());
                } else if (pageName === MARKETS) {
                  dispatch(clearMarket());
                }
              } else if (pageName === LEAGUES) {
                goBack(action);
              }

              return false;
            }}
          >
            {data && (
              <div
                className={classNames(
                  'overflow-hidden',
                  'w-100 h-100',
                  styles.backgroundColor,
                )}
              >
                <PageHeader
                  imageSrc={headerImageSrc}
                  title={title}
                  description={description}
                  hideHeader={rowIndex >= 1}
                  isVideo={isHeaderAnimated}
                  logoSrc={pageName === LEAGUES ? logoSrc : ''}
                />
                <InteriorRows
                  focusId="interior-rows"
                  trackingRowOffset={1}
                  onTileClick={onTileClick}
                  onInteriorRowsFocusIndexChanged={
                    onInteriorRowsFocusIndexChanged
                  }
                  rowData={rowData}
                  imageSrc={headerImageSrc}
                  title={title}
                  description={description}
                  relatedLeagues={relatedLeagues}
                  relatedShows={relatedShows}
                  relatedMarkets={relatedMarkets}
                  pageName={pageName}
                />
              </div>
            )}
            {!data && !loading && (
              <div
                className={classNames(
                  'w-100',
                  'h-100',
                  'd-flex',
                  'justify-content-center',
                  'align-items-center',
                  styles.noData,
                )}
              >
                {intl.formatMessage(messages.noData, { page: contentId })}
              </div>
            )}
          </FocusGroup>
        )}
      </FocusSettingsProvider>
    </WithLoading>
  );
};

InteriorPage.propTypes = {
  pageName: PropTypes.string,
  pageType: PropTypes.string,
};

InteriorPage.defaultProps = {
  pageName: null,
  pageType: null,
};

export default InteriorPage;
